/**
 * useAIGuidanceMode — Reads and updates the user's AI guidance preference.
 *
 * Modes:
 * - assistant: light suggestions, subtle drafting help
 * - copilot: proactive recommendations, stronger guidance
 * - minimal: AI only when explicitly invoked
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useOrg } from "@/contexts/OrgContext";
import { supabase } from "@/integrations/supabase/client";
import type { DynamicPayload } from "@/types/supabase-helpers";

export type AIGuidanceMode = "assistant" | "copilot" | "minimal";

export function useAIGuidanceMode() {
  const { user } = useAuth();
  const { orgId } = useOrg();
  const qc = useQueryClient();

  const { data: mode = "copilot" as AIGuidanceMode, isLoading } = useQuery({
    queryKey: ["ai_guidance_mode", orgId, user?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("organization_users")
        .select("ai_guidance_mode")
        .eq("organization_id", orgId)
        .eq("user_id", user!.id)
        .maybeSingle();
      return (data?.ai_guidance_mode as AIGuidanceMode) || "copilot";
    },
    enabled: !!orgId && !!user?.id,
  });

  const setMode = useMutation({
    mutationFn: async (newMode: AIGuidanceMode) => {
      await supabase
        .from("organization_users")
        .update({ ai_guidance_mode: newMode } as DynamicPayload)
        .eq("organization_id", orgId)
        .eq("user_id", user!.id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["ai_guidance_mode", orgId, user?.id] });
    },
  });

  /** Whether AI should show proactive suggestions */
  const isProactive = mode === "copilot";
  /** Whether AI suggestions should be shown at all */
  const showAI = mode !== "minimal";
  /** Whether to use full coaching tone */
  const isCoaching = mode === "copilot" || mode === "assistant";

  return {
    mode,
    setMode,
    isProactive,
    showAI,
    isCoaching,
    isLoading,
  };
}
